import React, { useCallback /* , useEffect */ } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  useDropzone,
  FileRejection,
  DropEvent,
  FileError,
} from 'react-dropzone';
import {
  uploadFiles,
  cancelFileUpload,
  uploadAttachment,
  cancelAttachmentUpload,
} from '../../redux/uploads/actions';
import {
  getUploadsByTag,
  getAttachmentUploadsByTag,
} from '../../redux/uploads/selectors';
import type {
  UploadSuccessCallback,
  UploadFailureCallback,
  UploadAttachmentSuccessCallback,
  AttachmentType,
} from '../../redux/uploads/types';
import { useVerification } from '../../VerificationPopup/withVerification';
import { MIME_CONFIG, mimeTypesToDropzoneAccept } from '../mime';

export interface FileUploadOptions {
  /** use to keep track of uploads belonging to this particular component */
  id?: string;
  /** if submitting an attachment we have to specify the parent deviation's id */
  parentDeviationId?: number;
  /** in case of an edit, deviation id we're replacing the image to */
  deviationId?: number;
  folderId?: number;
  /** for stash protect only */
  folderName?: string;
  acceptedMimeTypes?: string[];
  allowMultiple?: boolean;
  allowDragAndDrop?: boolean;
  allowClickOnRoot?: boolean;
  autoFetchDeviationOnSuccess?: boolean;
  isPreviewUpload?: boolean;
  isDraft?: boolean;
  isAttachment?: boolean;
  attachmentType?: AttachmentType;
  useDefaults?: boolean;
  enableStashProtectHacks?: boolean;
  onDrop?: (files: File[], ev: DropEvent) => boolean;
  /** https://react-dropzone.js.org/#src */
  validator?: (file: File) => FileError | FileError[] | null;
  /** https://react-dropzone.js.org/#src */
  onDropRejected?: (files: FileRejection[]) => void;
  onSuccess?: UploadSuccessCallback | UploadAttachmentSuccessCallback;
  onFailure?: UploadFailureCallback;
}

/**
 *  Basic file uploader to stash.
 *  Usage: add the input returned somewhere to the markup, and call showFilePicker to open the file picker programmatically.
 *  To create a drag-and-drop area, spread the props from getDropzoneRootProps into your element
 */
export function useFileUpload({
  id,
  parentDeviationId,
  deviationId,
  folderId,
  folderName,
  acceptedMimeTypes = MIME_CONFIG.forDeviationSubmit,
  allowMultiple = true,
  allowDragAndDrop = true,
  allowClickOnRoot = true,
  autoFetchDeviationOnSuccess = false,
  isDraft = false,
  isAttachment = false,
  attachmentType = 'pcp',
  useDefaults = false,
  isPreviewUpload = false,
  enableStashProtectHacks,
  onDrop,
  validator,
  onDropRejected: onDropRejectedProp,
  onSuccess,
  onFailure,
}: FileUploadOptions) {
  const dispatch = useDispatch();
  const { isVerificationNeeded, pushVerificationPopup } = useVerification();
  const activeUploads = useSelector(state =>
    isAttachment
      ? getAttachmentUploadsByTag(state, id)
      : getUploadsByTag(state, id)
  );

  const onFilePicked = useCallback(
    (files: File[], ev: DropEvent) => {
      if (isVerificationNeeded) {
        pushVerificationPopup();
        return;
      }

      if (!files || files.length < 1) {
        return;
      }

      if (onDrop && !onDrop(files, ev)) {
        return;
      }

      if (isAttachment) {
        files.reverse().forEach((file, idx) => {
          dispatch(
            uploadAttachment({
              tag: id,
              file,
              parentDeviationId: parentDeviationId!,
              type: attachmentType,
              onSuccess,
              onFailure,
            })
          );
        });
        return;
      }

      dispatch(
        uploadFiles(
          files.reverse().map((file, idx) => ({
            tag: id,
            file,
            deviationId,
            folderId,
            folderName: enableStashProtectHacks
              ? `${folderName}/${id}${(
                  Date.now() +
                  idx +
                  Math.round(Math.random() * 1000)
                ).toString(36)}`
              : undefined,
            autoFetchDeviationOnSuccess,
            isDraft,
            useDefaults,
            isPreviewUpload,
            onSuccess,
            onFailure,
          }))
        )
      );
    },
    [
      isVerificationNeeded,
      onDrop,
      dispatch,
      pushVerificationPopup,
      id,
      parentDeviationId,
      deviationId,
      folderId,
      enableStashProtectHacks,
      folderName,
      autoFetchDeviationOnSuccess,
      isDraft,
      isAttachment,
      attachmentType,
      useDefaults,
      isPreviewUpload,
      onSuccess,
      onFailure,
    ]
  );
  const onDropRejected = useCallback(
    // wrap so we don't need the onDropRejected prop for this component
    // to include the event
    (files: FileRejection[], dropevent: DropEvent) =>
      onDropRejectedProp?.(files),
    [onDropRejectedProp]
  );

  const cancelUpload = useCallback(
    uploadId =>
      dispatch(
        isAttachment
          ? cancelAttachmentUpload({ uploadId, type: attachmentType })
          : cancelFileUpload({ uploadId })
      ),
    [dispatch, isAttachment, attachmentType]
  );
  const acceptAll =
    acceptedMimeTypes.length === 1 && acceptedMimeTypes[0] === '*';
  const {
    getRootProps,
    getInputProps,
    open,
    isDragActive,
    isDragReject,
    isDragAccept,
  } = useDropzone({
    validator,
    onDropAccepted: onFilePicked,
    onDropRejected,
    accept: acceptAll
      ? undefined
      : mimeTypesToDropzoneAccept(acceptedMimeTypes),
    multiple: allowMultiple,
    noDrag: !allowDragAndDrop,
    noClick: !allowClickOnRoot,
  });

  const input = <input {...getInputProps()} />;

  return {
    showFilePicker: open,
    input,
    activeUploads,
    cancelUpload,
    getDropzoneRootProps: getRootProps,
    isDragging: isDragActive,
    isDragAccept,
    isDragReject,
  };
}
